<template>
    <div>
        <textarea name="focus" id="quitfocus" rows="3" style="display: none"></textarea>
        <vs-row>
            <vs-col vs-w="6">
                <vs-button
                    v-if="currentStep > 0"
                    id="prev2"
                    class="mr-2"
                    color="primary"
                    type="filled"
                    icon-pack="feather"
                    icon="icon-chevron-left"
                    @click.native="prevClickHandle"
                >
                    Précédent
                </vs-button>

                <vs-button
                    id="goBack"
                    color="primary"
                    type="border"
                    icon-pack="feather"
                    icon="icon-arrow-left"
                    :href="backUrl"
                >
                    Sortir
                </vs-button>
            </vs-col>
            <vs-col vs-w="6" vs-type="flex" vs-justify="flex-end">
                <vs-button
                    v-if="!isLastStep"
                    id="next2"
                    color="primary"
                    type="filled"
                    icon-pack="feather"
                    icon="icon-chevron-right"
                    @click.native="nextClickHandle"
                >
                    Suivant
                </vs-button>
                <vs-button
                    v-else
                    id="submit2"
                    color="primary"
                    type="filled"
                    icon-pack="feather"
                    icon="icon-check"
                    @click="nextClickHandle"
                >
                    <span v-if="typeForm === 'consultation'">
                        Retour
                    </span>
                    <span v-else-if="skillReview">
                        <template v-if="canValidateSkillReview">
                            <div>
                                Valider le {{ skillRatingNames.singular.toLowerCase() }}
                            </div>
                        </template>
                        <template v-else>
                            <div>
                                Soumettre pour validation
                            </div>
                        </template>
                    </span>
                    <span v-else>
                        <template v-if="canValidateProject">
                            <div>
                                Valider le {{ projectNames.singular.toLowerCase() }}
                            </div>
                        </template>
                        <template v-else>
                            <div>
                                Soumettre pour validation
                            </div>
                        </template>
                    </span>
                </vs-button>
            </vs-col>
        </vs-row>
        <div v-if="employee" class="mt-3">
            <form-wizard
                ref="formWizard"
                color="rgba(var(--vs-primary), 1)"
                error-color="rgba(var(--vs-danger), 1)"
                :start-index="currentStep"
                :subtitle="null"
                @on-complete="endFormHandle"
                @on-validate="validateFormHandle"
                @on-error="errorFormHandle"
                @on-change="changePageHandle"
            >
                <h4 slot="title" class="wizard-custom-title">
                    {{ titleWord }} de <b>{{ titleName }}</b> du {{ titleDate }} {{ titleComplement }} ({{ titleEstablishmentsServicesNames }})
                </h4>

                <!-- Bouton sauvegarder statique -->
                <vs-button
                    id="saveQuit"
                    v-if="typeForm !== 'consultation'"
                    color="primary"
                    type="filled"
                    icon-pack="feather"
                    icon="icon-save"
                    @click.native="saveProjectClickHandle"
                >
                    Sauvegarder
                </vs-button>
                
                <!-- Alerte erreur de validation -->
                <div v-if="errorValidateMessage" class="mb-10">
                    <vs-alert color="danger" active="true">
                        {{ errorValidateMessage }}
                    </vs-alert>
                </div>

                <!-- Tab: Périmètre -->
                <tab-content
                    title="Périmètre"
                    class="mb-5"
                    icon="feather icon-crosshair"
                    :before-change="validatePerimeter"
                >
                    <form :data-vv-scope="`step-${getStepFromName('perimeter')}`">
                        <Perimeter-Tab
                            :type-form="typeForm"
                            :skill-review="skillReview"
                            v-model="resource"
                            :jobs="orderedJobs"
                            :occupations="orderedOccupations"
                            :defaultOccSkillPerimeter="lastRating.perimeter_occskills || []"
                            :externals-usage-values="externalsUsageValues"
                            :employeePerimeter="{
                                employeeJobs: employeeJobs,
                                employeeOccupations: employeeOccupations,
                                employeeOccupationSkills: employeeOccupationSkills
                            }"
                            @career-updated="careerUpdated = true"
                            @initial-loading="initialLoadingPerimeter = false"
                        ></Perimeter-Tab>
                    </form>
                </tab-content>

                <!-- Tab: Evaluation des savoir-faire -->
                <tab-content
                    title="Évaluation des savoir-faire"
                    class="mb-5"
                    icon="feather icon-aperture"
                    :before-change="validateOccupationSkills"
                >
                    <form :data-vv-scope="`step-${getStepFromName('occupation-skills')}`">
                        <Occupation-Skills-Rating-Tab
                            v-if="currentStep == getStepFromName('occupation-skills')"
                            v-model="resource"
                            :type-form="typeForm"
                            :skill-review="skillReview"
                            :key="updateRatingList"
                            :jobColors="jobColors"
                            :occupationColors="occupationColors"
                        ></Occupation-Skills-Rating-Tab>
                    </form>
                </tab-content>

                <!-- Évaluation des compétences -->
                <tab-content
                    title="Évaluation des compétences"
                    class="mb-5"
                    icon="feather icon-award"
                    :before-change="validateSkills"
                >
                    <form :data-vv-scope="`step-${getStepFromName('skills')}`">
                        <Skills-Rating-Tab
                            v-if="currentStep == getStepFromName('skills') && !occupationSkillOnly"
                            v-model="resource"
                            :type-form="typeForm"
                            :skill-review="skillReview"
                            :admin-skills="adminSkills"
                            :colors="colors.data"
                            :key="updateRatingList"
                            :jobColors="jobColors"
                            :occupationColors="occupationColors"
                        ></Skills-Rating-Tab>
                    </form>
                </tab-content>

                <!-- Auto-évaluation savoir-faire-->
                <tab-content
                    v-if="!skillReview && occupationSkillsSeftRating"
                    title="Auto-évaluation savoir-faire"
                    class="mb-5"
                    icon="feather icon-check-square"
                    :before-change="validateSelfOccupationSkills"
                >
                    <form :data-vv-scope="`step-${getStepFromName('self-occupation-skills')}`">
                        <SelfOccupationSkillsRatingTab
                            v-if="currentStep == getStepFromName('self-occupation-skills')"
                            v-model="resource"
                            :type-form="typeForm"
                            :skill-review="skillReview"
                            :self-rating-source-values="selfRatingSourceValues"
                            :key="updateRatingList"
                            :jobColors="jobColors"
                            :occupationColors="occupationColors"
                        />
                    </form>
                </tab-content>

                <!-- Auto-évaluation compétence -->
                <tab-content
                    v-if="!skillReview"
                    title="Auto-évaluation compétence"
                    class="mb-5"
                    icon="feather icon-user-check"
                    :before-change="validateSelfSkills"
                >
                    <form :data-vv-scope="`step-${getStepFromName('self-skills')}`">
                        <Self-Rating-Tab
                            v-if="!occupationSkillOnly && currentStep == getStepFromName('self-skills')"
                            v-model="resource"
                            :type-form="typeForm"
                            :skill-review="skillReview"
                            :self-rating-source-values="selfRatingSourceValues"
                            :admin-skills="adminSkills"
                            :colors="colors.data"
                            :key="updateRatingList"
                            :jobColors="jobColors"
                            :occupationColors="occupationColors"
                        ></Self-Rating-Tab>
                    </form>
                </tab-content>

                <!-- Tab: Souhaits d'évolution et objectifs-->
                <tab-content
                    title="Objectifs & Synthèse"
                    class="mb-5"
                    icon="feather icon-briefcase"
                    :before-change="validateGoalsAndSynthesis"
                >
                    <form :data-vv-scope="`step-${getStepFromName('goals-and-synthesis')}`">
                        <Goals-And-Synthesis-Tab
                            v-if="currentStep == getStepFromName('goals-and-synthesis')"
                            v-model="resource"
                            :type-form="typeForm"
                            :jobs="orderedJobs"
                            :occupations="orderedOccupations"
                            :jobColors="jobColors"
                            :occupationColors="occupationColors"
                            :states="states"
                        ></Goals-And-Synthesis-Tab>
                    </form>
                </tab-content>

                <!-- Actions -->
                <template slot="footer" slot-scope="props">
                    <div class="wizard-footer-left" onclick="window.scrollTo(0,0)">
                        <vs-button
                            v-if="props.activeTabIndex > 0"
                            id="prev"
                            color="primary"
                            type="filled"
                            icon-pack="feather"
                            icon="icon-chevron-left"
                            @click.native="prevClickHandle"
                        >
                            Précédent
                        </vs-button>
                    </div>

                    <div class="wizard-footer-right" onclick="window.scrollTo(0,0)">
                        <vs-button
                            v-if="!props.isLastStep"
                            id="next"
                            class="wizard-footer-right"
                            color="primary"
                            type="filled"
                            icon-pack="feather"
                            icon="icon-chevron-right"
                            @click.native="nextClickHandle"
                        >
                            Suivant
                        </vs-button>
                        <vs-button
                            v-else
                            id="submit"
                            class="wizard-footer-right finish-button"
                            color="primary"
                            type="filled"
                            icon-pack="feather"
                            icon="icon-check"
                            @click.native="nextClickHandle"
                        >
                            <span v-if="typeForm === 'consultation'">
                                Retour
                            </span>
                            <span v-else-if="skillReview">
                                <template v-if="canValidateSkillReview">
                                    <div>
                                        Valider le {{ skillRatingNames.singular.toLowerCase() }}
                                    </div>
                                </template>
                                <template v-else>
                                    <div>
                                        Soumettre pour validation
                                    </div>
                                </template>
                            </span>
                            <span v-else>
                                <template v-if="canValidateProject">
                                    <div>
                                        Valider le {{ projectNames.singular.toLowerCase() }}
                                    </div>
                                </template>
                                <template v-else>
                                    <div>
                                        Soumettre pour validation
                                    </div>
                                </template>
                            </span>
                        </vs-button>
                    </div>
                </template>

            </form-wizard>
        </div>
    </div>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";

import PerimeterTab from './partials/tabs/PerimeterTab.vue';
import OccupationSkillsRatingTab from './partials/tabs/OccupationSkillsRatingTab.vue';
import SelfOccupationSkillsRatingTab from './partials/tabs/SelfOccupationSkillsRatingTab.vue';
import SkillsRatingTab from './partials/tabs/SkillsRatingTab.vue';
import SelfRatingTab from './partials/tabs/SelfRatingTab.vue';
import GoalsAndSynthesisTab from './partials/tabs/GoalsAndSynthesisTab.vue';
import ratingService from "@components/services/ratings.js";

import "vue-form-wizard/dist/vue-form-wizard.min.css";

export default {
    components: {
        FormWizard,
        TabContent,
        PerimeterTab, OccupationSkillsRatingTab, SelfOccupationSkillsRatingTab, SkillsRatingTab, SelfRatingTab, GoalsAndSynthesisTab
    },
    props: {
        baseUrl: {
            type: String,
            required: true
        },
        backUrl: {
            type: String,
            required: true
        },
        typeForm: {
            type: String,
            required: true
        },
        employee: {
            type: Object,
            required: true
        },
        employeeRating: {
            type: Object,
            required: true
        },
        skillReview: {
            type: Boolean,
            required: true
        },
        experienceTypes: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            init: true,
            initialLoadingPerimeter: true,
            log: console.log,
            currentStep: 0,
            maxStep: 0,
            lastStep: 0,
            errorValidateMessage: null,
            resource: {},
            initalResource: {},
            jobs: {
                loading: true,
                data: []
            },
            occupations: {
                loading: true,
                data: []
            },
            grids: {
                loading: true,
                data: []
            },
            establishments: {
                loading: true,
                data: []
            },
            adminSkills: {
                loading: true,
                data: []
            },
            colors: {
                loading: true,
                data: []
            },
            states: {
                loading: true,
                data: []
            },
            careerUpdated: false,
            ratingState: null,
            selfRatingSourceValues: {
                LAST_SELF: 1,
                MOBILITY: 2
            },
            externalsUsageValues: {
                FILL: 1,
                DISPLAY: 2
            },
            lastRating: {},
            skillSteps: [2, 3],
            targetDebugStep: 0, // DEV - Utilisé pour le DEBUG
            updateRatingList: 0,
            employeeJobs: [],
            employeeOccupations: [],
            employeeOccupationSkills: [],
          };
    },

    created() {
        let start = Date.now()
        this.initResource();

        this.resource.hasInternalRatings = false

        if (this.typeForm === 'creation') {
            this.loadLastRating().then(() => {
                this.initResourceFromLastRating();
            });
        }
        else if (this.typeForm === 'edition' || this.typeForm === 'poursuite') {
            this.resource.hasInternalRatings = true
        }

        if (this.occupationSkillsSeftRating) {
            this.skillSteps = this.skillReview ? [2] : [2, 4]
        }

        // Chargement de base pour la partie « Périmètre »
        Promise.all([
            this.loadEmployeeJobs(),
            this.loadEmployeeOccupations(),
            this.loadEmployeeOccupationSkills(),    
            this.loadJobs(),
            this.loadGrids(),
            this.loadOccupations(),
            this.loadEstablishments(),
            this.loadStates()
        ]).then(() => {
            console.info('=> Après chargement des listes : +' + (Date.now() - start) + 'ms');
            this.init = false;

            let start2 = Date.now()
            Promise.all([
                this.loadAdminSkill(),
                this.loadColors()
            ]).then(() => {
                console.info('=> Chargement des données supplémentaires : +' + (Date.now() - start2) + 'ms');
            })
        })

        console.info('=> Chargement de base : ' + (Date.now() - start) + 'ms');

        this.ratingState = this.typeForm;

        // --> Utilisé pour le débug
        if (this.targetDebugStep && this.targetDebugStep > 1) {
            setTimeout(() => {
                document.getElementById('next').click();
            }, 800);
        }
    },

    mounted() {
        this.lastStep = this.$refs.formWizard.tabCount - 1;
    },

    updated() {
        if (this.occupationSkillOnly) {
                
            // On cache les étapes de compétences
            this.skillSteps.forEach(item => {
                const tab = document.querySelector(`.wizard-nav li:nth-child(${item + 1})`);
                tab.classList.add('disabled-tab')
            })
 
        }
    },
    computed: {
        isLastStep() {
            return this.currentStep == this.lastStep;
        },
        canValidateSkillReview() {
            return this.canMulti('update', 'skill_review_validations', this.employee.active_services.map(s => s.id));
        },
        canValidateProject() {
            return this.canMulti('update', 'rating_validations', this.employee.active_services.map(s => s.id));
        },
        titleDate() {
            return this.$moment(this.employeeRating.rating_date).format("l");
        },
        titleWord() {
            let word =  this.skillReview ? this.skillRatingNames.singular : this.projectNames.singular;
            switch (this.ratingState) {
                case "consultation":
                    word = "Consultation du "+word;
                    break;
                case "validation":
                    word = "Validation du "+word;
                    break;
                case "edition":
                    word = "Modification du "+word;
                    break;
                case "poursuite":
                    word = "Poursuite du "+word;
                    break;
                default:
                    word = "Nouveau "+word;
                    break;
            }

            return word
        },
        titleComplement() {
            return this.typeForm === "consultation" ? "(validé)" : ""
        },
        titleName() {
            return this.employee.first_name + " " + this.employee.last_name;
        },
        titleEstablishmentsServicesNames() {
            return this.employee.active_services.map(s => s.establishment.name+': '+s.title).join(' | ')
        },
        currentJobs() {
            return this.employee.current_jobs;
        },
        currentOccupations() {
            return this.employee.current_occupations;
        },

        gridsByType() {
            var gridsByType = {};
            this.grids.data.filter(g => !g.deleted_at).sort((a, b) => a.type > b.type ? 1 : -1).forEach(grid => {
                if (!gridsByType[grid.type]) {
                    gridsByType[grid.type] = [];
                }
                gridsByType[grid.type].push(grid)
            })

            return gridsByType
        },

        orderedJobs() {
            let jobsSorted = [];

            this.establishments.data.forEach(e => {
                e.services.forEach(s => {

                    const jobs = this.jobs.data
                                    .filter(j => j.service_id == s.id)
                                    .filter(j => !j.deleted_at)
                                    .sort(function(a, b) {
                                        if (a.title > b.title) return 1;
                                        if (a.title < b.title) return -1;
                                        return 0;
                                    });

                    jobsSorted.push({
                        establishmentService: `${e.name} - ${s.title}`,
                        jobs: jobs
                    });
                });
            });

            jobsSorted = jobsSorted.sort(function(a, b) {
                if (a.establishmentService > b.establishmentService) return 1;
                if (a.establishmentService < b.establishmentService) return -1;
                return 0;
            });

            // Ajout des grilles
            Object.entries(this.gridsByType).forEach(([type, values]) => {
                jobsSorted.push({
                    establishmentService: `Grilles - ${type}`,
                    jobs: values.sort((a, b) => a.title > b.title ? 1 : -1)
                });
            })

            return jobsSorted;
        },
        orderedOccupations() {
            let occupationsTypes = [];

            this.occupations.data.forEach(o => {
                if (!o.deleted_at) {
                    let index = occupationsTypes.findIndex(t => t.type === o.type);
                    if (index == -1) {
                        occupationsTypes.push({
                            type: o.type,
                            occupations: [o]
                        });
                    } else {
                        occupationsTypes[index].occupations.push(o);
                    }
                }
            });

            occupationsTypes = occupationsTypes.map(type => {
                    type.occupations = type.occupations.sort(function(a, b) {
                        if (a.type > b.type) return 1;
                        if (a.type < b.type) return -1;
                        return 0;
                    });
                    return type;
                })
                .sort(function(a, b) {
                    if (a.type > b.type) return 1;
                    if (a.type < b.type) return -1;
                    return 0;
                });


            // Ajout des grilles
            Object.entries(this.gridsByType).forEach(([type, values]) => {
                occupationsTypes.push({
                    type: `Grilles - ${type}`,
                    occupations: values.sort((a, b) => a.title > b.title ? 1 : -1)
                });
            })

            return occupationsTypes;
        },
        jobColors() {
            let colors = []
            this.resource.perimeter_jobs.forEach((job, index) => {
                colors.push({
                    id: job.id,
                    color: this.getColorFor('job', index+1)
                })
            })

            return colors
        },
        occupationColors() {
            let colors = []
            this.resource.perimeter_occupations.forEach((occupation, index) => {
                colors.push({
                    id: occupation.id,
                    admin_id: occupation.admin_id,
                    color: this.getColorFor('occupation', index+1)
                })
            })

            return colors
        },
        occupationSkillOnly() {
            return this.employee.occupation_skills_rating_only;
        },
        occupationSkillsSeftRating() {
            return this.employee.occupation_skills_self_rating;
        }
    },
    watch: {
        resource: {
            handler: function(value) {
                console.log('Watch Global Resource', value)
                
            },
            deep: true
        },
    },
    methods: {

        initResource() {
            this.resource = window._.cloneDeep(this.employeeRating);

            if (!this.resource.comment || Object.values(this.resource.comment).length == 0) {
                this.resource.comment = {};
            }

            if (!this.resource.employee) {
                this.resource.employee = window._.cloneDeep(this.employee);
            }

            if (!this.resource.externals) {
                this.resource.externals = [];
            }

            const jobGrids = this.filterGridsByJobType(this.employee.current_grids).map(g => {
                g.uid = `g-${g.id}`
                g.title = `${g.title} (${g.sector.title})`
                return g;
            });

            if (!this.resource.perimeter_jobs) {
                this.resource.perimeter_jobs = [...this.employee.current_jobs, ...jobGrids];
            } else {
                
                const pJobs = this.resource.perimeter_jobs.map(j => {
                    j.uid = `j-${j.id}`
                    return j;
                })

                const pJobGrids = this.filterGridsByJobType(this.resource.perimeter_grids).map(g => {
                    g.uid = `g-${g.id}`
                    g.title = `${g.title} (${g.sector.title})`
                    return g;
                });

                this.resource.perimeter_jobs = [...pJobs, ...pJobGrids]
            }

            if (!this.resource.perimeter_occskills) {
                // Cas d'une création (init de depuis le parcours)
                this.resource.perimeter_occskills = [
                    ...this.employee.current_occupation_skills,
                    ...this.employee.current_admin_occupation_skills.map(aos => {
                        aos.title = `${aos.title} (${aos.type})`
                        return aos
                    })
                ];
            } else {
                // Cas d'une mise à jour (init depuis le périmètre)
                this.resource.perimeter_occskills = [
                    ...this.resource.perimeter_occskills, 
                    ...this.resource.perimeter_adminoccskills.map(aos => {
                        aos.title = `${aos.title} (${aos.type})`
                        return aos
                    })
                ]
            }

            const occupationGrids = this.filterGridsByOccupationType(this.employee.current_grids).map(g => {
                g.uid = `g-${g.id}`
                g.title = `${g.title} (${g.sector.title})`
                return g;
            });

            if (!this.resource.perimeter_occupations) {
                this.resource.perimeter_occupations = [...this.employee.current_occupations, ...occupationGrids];
            } else {
                
                const pOccupation = this.resource.perimeter_occupations.map(o => {
                    o.uid = `o-${o.id}`
                    return o
                })

                const pOccupationGrids = this.filterGridsByOccupationType(this.resource.perimeter_grids).map(g => {
                    g.uid = `g-${g.id}`
                    g.title = `${g.title} (${g.sector.title})`
                    return g;
                });

                this.resource.perimeter_occupations = [...pOccupation, ...pOccupationGrids]
            }

            if (!this.resource.personals) {
                this.resource.personals = [];
            }

            if (!this.resource.externalsUsage) {
                this.resource.externalsUsage = this.externalsUsageValues.FILL;
            }

            if (!this.resource.occ_skills) {
                this.resource.occupations_skills = [];
            } else {
                // Fix - pour le problème de relation pivot (Morph)
                this.resource.occupations_skills = this.resource.occ_skills;
            }

            if (!this.resource.self_occupation_skills) {
                this.resource.self_occupation_skills = [];
            } else {
                if (this.resource.self_occ_skills) {
                    // Fix - pour le problème de relation pivot (Morph)
                    this.resource.self_occupation_skills = this.resource.self_occ_skills;
                }
            }
            if (!this.resource.skills) {
                this.resource.skills = [];
            }

            if (!this.resource.self_skills) {
                this.resource.self_skills = [];
            }

            if (!this.resource.comment) {
                this.resource.comment = {};
            }

            // WISHLIST JOBS
            if (!this.resource.wishlist_jobs) {
                this.resource.wishlist_jobs = [];
            }
            
            const wishlistJobs = this.resource.wishlist_jobs.map(j => {
                j.uid = `j-${j.id}`
                return j;
            })

            const wishlistJobGrids = this.filterGridsByJobType(this.resource.wishlist_grids).map(g => {
                g.uid = `g-${g.id}`
                g.title = `${g.title} (${g.sector.title})`
                return g;
            });

            this.resource.wishlist_jobs = [...wishlistJobs, ...wishlistJobGrids]

            // WISHLIST OCCUPATIONS
            if (!this.resource.wishlist_occupations) {
                this.resource.wishlist_occupations = [];
            }

            const wishlistOccupation = this.resource.wishlist_occupations.map(o => {
                o.uid = `o-${o.id}`
                return o
            })

            const wishlistOccupationGrids = this.filterGridsByOccupationType(this.resource.wishlist_grids).map(g => {
                g.uid = `g-${g.id}`
                g.title = `${g.title} (${g.sector.title})`
                return g;
            });

            this.resource.wishlist_occupations = [...wishlistOccupation, ...wishlistOccupationGrids]

            // GOALS
            if (!this.resource.occupation_skills_goals) {
                this.resource.occupation_skills_goals = [];
            }

            if (!this.resource.admin_occupation_skills_goals) {
                this.resource.admin_occupation_skills_goals = [];
            }

            if (!this.resource.skills_goals) {
                this.resource.skills_goals = [];
            }

            if (!this.resource.others_goals) {
                this.resource.others_goals = [];
            }

            if (!this.resource.employee.services) {
                this.resource.employee.services = this.resource.employee.active_services
            }

            if (!this.resource.employee.establishments) {
                this.resource.employee.establishments = this.resource.employee.active_establishments
            }

            this.resource.allSkills = true;

            this.initalResource = window._.cloneDeep(this.resource);
        },

        resetResourceEmployee() {
            this.resource.employee = JSON.parse(JSON.stringify(this.employee));
            this.resource.employee.services = this.resource.employee.active_services;
            this.resource.employee.establishments = this.resource.employee.active_establishments;
        },

        // TODO - Utiliser le Mixin
        filterGridsByJobType(grids) {
            if (!grids || grids.length === 0) {
                return [];
            }

            const jobExperienceType = this.experienceTypes.find(et => et.slug == 'PRES')

            return grids.filter(g => g.pivot.experience_type_id === jobExperienceType.id);
        },
        // TODO - Utiliser le Mixin
        filterGridsByOccupationType(grids) {
            if (!grids || grids.length === 0) {
                return [];
            }

            const occupationExperienceType = this.experienceTypes.find(et => et.slug == 'ME')

            return grids.filter(g => g.pivot.experience_type_id === occupationExperienceType.id);
        },

        async loadEstablishments() {
            return window
                .axios({
                    method: "get",
                    url: "/api/referentiel/establishments",
                    params: {
                        perpage: 9999,
                        include: ['services']
                    }
                })
                .then(response => {
                    this.establishments.data = response.data.data;
                    this.establishments.loading = false;
                })
                .catch(err => {
                    this.notifyError(err, `Une erreur est survenue`);
                });
        },

        async loadStates() {
            return window
                .axios({
                    method: "get",
                    url: `/api/referentiel/rating-states`,
                    params: {}
                })
                .then(response => {
                    this.states.data = response.data;
                    this.states.loading = false;
                })
                .catch(err => {
                    console.log(err);
                    this.notifyError(err, `Une erreur est survenue`);
                });
        },

        async loadJobs() {
            return window
                .axios({
                    method: "get",
                    url: "/api/referentiel/jobs",
                    params: {
                        perpage: 9999,
                        include: [
                            'service.establishment',
                            'occupation_skills'
                        ],
                        withTrash: 'true',
                    }
                })
                .then(response => {
                    let jobs = response.data.data
                    this.jobs.data = jobs.map(j => {
                        j.uid = `j-${j.id}`
                        return j
                    })
                    this.jobs.loading = false;
                })
                .catch(err => {
                    this.notifyError(err, `Une erreur est survenue`);
                    console.error(err)
                });
        },

        async loadOccupations() {
            return window
                .axios({
                    method: "get",
                    url: "/api/referentiel/occupations",
                    params: {
                        perpage: 9999,
                        include: [
                            "sector",
                            "occupation_skills",
                            "admin_occupation_skills",
                        ],
                        withTrash: 'true',
                    }
                })
                .then(response => {
                    let occupations = response.data.data
                    this.occupations.data = occupations.map(o => {
                        o.uid = `o-${o.id}`
                        return o
                    })
                    this.occupations.loading = false;
                })
                .catch(err => {
                    this.notifyError(err, `Une erreur est survenue`);
                    console.error(err)
                });
        },

        async loadGrids() {
            return window
                .axios({
                    method: "get",
                    url: "/api/referentiel/grids",
                    params: {
                        perpage: 9999,
                        include: [
                            "sector"
                        ]
                    }
                })
                .then(response => {
                    this.grids.data = response.data.data.map(g => {
                        g.uid = `g-${g.id}`
                        g.title = `${g.title} (${g.sector.title})`
                        return g;
                    })
                    this.grids.loading = false;
                })
                .catch(err => {
                    this.notifyError(err, `Une erreur est survenue`);
                    console.error(err)
                });
        },

        async loadAdminSkill() {
            return window
                .axios({
                    method: "get",
                    url: "/api/admin/admin-skills",
                    params: {
                        perpage: 9999,
                        include: [],
                        withTrash: 'true',
                    }
                })
                .then(response => {
                    let adminSkills = response.data.data
                    this.adminSkills.data = adminSkills
                    this.adminSkills.loading = false;
                })
                .catch(err => {
                    this.notifyError(err, `Une erreur est survenue`);
                    console.error(err)
                });
        },

        async loadColors() {
            return window
                .axios({
                    method: "get",
                    url: "/api/referentiel/settings/colors",
                    params: { perpage: 9999 }
                })
                .then(response => {
                    let colors = response.data;

                    this.colors.data = colors
                    this.colors.loading = false;
                })
                .catch(err => {
                    console.log(err);
                    this.notifyError(err, `Une erreur est survenue`);
                });
        },

        loadEmployeeJobs() {
            // Chargement des postes occupés
            return window.axios.get('/api/gestion/employees/' + this.employee.id + '/jobs').then(response => {
                this.employeeJobs = response.data.filter(job => !!job.job);
            });
        },

        loadEmployeeOccupations() {
            // Chargement des métiers occupés
            return window.axios.get('/api/gestion/employees/' + this.employee.id + '/occupations').then(response => {
                this.employeeOccupations = response.data.filter(occupation => !!occupation.occupation);
            });
        },

        loadEmployeeOccupationSkills() {
            // Chargement des autres savoir-faire
            return window.axios.get('/api/gestion/employees/' + this.employee.id + '/occupation-skills').then(response => {
                this.employeeOccupationSkills = response.data;
            });
        },

        changePageHandle(prevIndex, nextIndex) {

            // Suivant vers compétence ou auto-eval
            if (this.occupationSkillOnly && prevIndex < nextIndex && (this.skillSteps.includes(nextIndex))) {
                // TODO - Bug sur click avance du wizard. Je n'ai pas trouvé comment le détecter.
                setTimeout(() => {
                    this.$refs.formWizard.nextTab();
                }, 10);
            }

            // Précédent vers compétence ou auto-eval
            if (this.occupationSkillOnly && prevIndex > nextIndex && (this.skillSteps.includes(nextIndex))) {
                setTimeout(() => {
                    this.$refs.formWizard.prevTab();
                }, 10)
            }
            
            this.currentStep = nextIndex;

            if (this.maxStep < nextIndex) {
                this.maxStep = nextIndex;
            }

            // Utilisé pour le débug
            if (this.targetDebugStep && this.targetDebugStep > nextIndex) {
                setTimeout(() => {
                    this.$refs.formWizard.nextTab();
                }, 1500);
            }
        },

        /**
         * Bouton Sauvegarder
         */
        async saveProjectClickHandle() {

            setTimeout(() => {

                const hasOccupationsSkillToValidate = this.resource.occupations_skills.filter(os => os.to_validate == true).length > 0;
                const hasSkillToValidate = this.resource.skills.filter(os => os.to_validate == true).length > 0;
    
                // if (this.skillReview) {
                //     this.resource.status = 0
                //     this.employeeRating.status = 0; //force
                // }
    
                if (this.typeForm == "edition" && this.employeeRating.status == -1) {
                    this.$swal(
                        "Sauvegarde impossible car un "+this.projectNames.singular+" est déjà en cours",
                        "",
                        "warning"
                    );
                    return;
                } else if (hasOccupationsSkillToValidate || hasSkillToValidate) {
    
                    // Tous les éléments de la page n'ont pas été validés
                    this.$swal({
                        title: `La sauvegarde va valider toutes les différences. Souhaitez-vous continuer ?`,
                        text: "",
                        type: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Ok",
                        cancelButtonText: "Annuler"
                    }).then(result => {
                        if (result.value) {
                            console.log(this.resource.status)
                            this.saveRating({ redirect: false });
                            this.resource.occupations_skills.map(os => os.to_validate = false);
                            this.resource.skills.map(os => os.to_validate = false)
                            this.updateRatingList++;
                        }
                    });
                } else if (
                    !this.skillReview &&
                    (this.typeForm == "creation" ||
                        this.typeForm == "edition" ||
                        this.typeForm == "poursuite") &&
                    this.selfRatingSource === this.selfRatingSourceValues.MOBILITY
                ) {
                    /*this.$swal({
                        title: `Confirmez-vous vouloir utiliser l'auto-évaluation Portail de l'usager pour l'auto-évaluation ?`,
                        text: "",
                        type: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Ok",
                        cancelButtonText: "Annuler"
                    }).then(result => {
                        if (result.value) {
                            this.saveRating({redirect: false});
                        }
                    });*/
                    this.saveRating({redirect: false, saveOnly: true});
                } else if (this.skillReview && false) {
                    this.saveRating({ redirect: false, waitingValidation: true, saveOnly: true });
                } else if (this.employeeRating.status == 0) {
                    this.saveRating({ redirect: false, waitingValidation: true, saveOnly: true });
                } else {
                    this.saveRating({ redirect: false, saveOnly: true });
                }
            }, 1000)

        },

        /**
         * Evénement à la fin du formulaire
         */
        endFormHandle() {
            if (this.typeForm == "consultation") {

                // Retour
                this.redirectBack();
            }  else if (this.canValidateSkillReview && this.skillReview) {

                // Validation du bilan
                this.$swal({
                    title: `Êtes-vous sûr de vouloir valider le ${this.skillRatingNames.singular} ? Il ne sera plus modifiable.`,
                    text: "",
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Ok",
                    cancelButtonText: "Annuler"
                }).then(result => {
                    if (result.value) {
                        this.localLoading();
                        this.saveRating({redirect: true, validation: true});
                    }
                });
            } else if (this.canValidateProject && !this.skillReview) {

                // Validation du projet personnalisé
                this.$swal({
                    title: `Êtes-vous sûr de vouloir valider le ${this.projectNames.singular} ? Il ne sera plus modifiable.`,
                    text: "",
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Ok",
                    cancelButtonText: "Annuler"
                }).then(result => {
                    if (result.value) {
                        this.localLoading();
                        this.saveRating({redirect: true, validation: true});
                    }
                });
            } else {
                // Soumettre pour validation
                this.localLoading();
                this.saveRating({ redirect: true, waitingValidation: true});
            }
        },

        /**
         * Sauvegarde
         */
        async saveRating({ redirect = true, validation = false, waitingValidation = false, saveOnly = false }) {

            let successMessage = '';

            if (validation) {
                this.resource.status = 1;
                this.ratingState = 'validation';
            } else if (waitingValidation) {
                this.resource.status = 0;
                this.ratingState = 'validation';
            } else {
                if (!this.resource.status) {
                    this.resource.status = -1;
                }
            }

            // if (this.skillReview) {
            //     this.resource.status = 0; //force
            // }

            // Filtrage des objectifs non choisis
            this.resource.occupation_skills_goals = this.resource.occupation_skills_goals?.filter(s => s.occupationSkill != null)
            this.resource.skills_goals = this.resource.skills_goals?.filter(s => s.skill != null)

            // Affectation d'un thème par défault pour les Objectifs sans Thème
            // + Affectation d'un état par défaut si Objectif sans Etat
            this.resource.occupation_skills_goals.forEach(osg => {
                if (typeof osg.theme !== 'string' || osg.theme.trim().length === 0) {
                    osg.theme = 'Savoir-faire'
                }
                if (!osg.state) {
                    osg.state = this.states.data[0]?.id
                }
            })
            this.resource.skills_goals.forEach(osg => {
                if (typeof osg.theme !== 'string' || osg.theme.trim().length === 0) {
                    osg.theme = 'Compétence'
                }
                if (!osg.state) {
                    osg.state = this.states.data[0]?.id
                }
            })
            this.resource.others_goals.forEach(osg => {
                if (typeof osg.theme !== 'string' || osg.theme.trim().length === 0) {
                    osg.theme = 'Autre'
                }
                if (!osg.state) {
                    osg.state = this.states.data[0]?.id
                }
            })

            if (saveOnly) {
                successMessage = "Modification enregistrée"
            } else {

                switch (this.ratingState) {
                    case "creation":
                        successMessage = `${this.skillReview ? this.skillRatingNames.singular : this.projectNames.singular} créé et passé au statut "En cours"`;
                        break;
                    case "poursuite":
                        successMessage = "Progression enregistrée";
                        break;
                    case "edition":
                        successMessage = "Modification enregistrée"
                        break;
                    case "validation":
                        successMessage = `${this.skillReview ? this.skillRatingNames.singular : this.projectNames.singular} validé avec succès !`
                        break;
                    default:
                        break;
                }
            }

            // Enregistrement du projet en BDD
            this.store().then(response => {

                const result = response.data;
                this.resource.id = result.id;

                if (this.ratingState == 'creation') {
                    this.ratingState = 'poursuite';
                }

                this.notifySuccess("Succès !", successMessage);

                if (this.careerUpdated) {
                    this.updateCareerValues((p) => {}, (p) => {})
                }

                if (redirect) {
                    this.redirectBack();
                }
            });
        },

        store() {
            let method = 'post';
            let url = `/api/gestion/employees/${this.employee.id}/ratings`;
            
            let data = window._.cloneDeep(this.resource);

            data.employee = {id: data.employee.id}
            data.perimeter_jobs = data.perimeter_jobs.map(j => ({id: j.id, resource: j.resource}))
            data.perimeter_occupations = data.perimeter_occupations.map(j => ({id: j.id, resource: j.resource}))
            data.perimeter_occskills = data.perimeter_occskills.map(j => ({id: j.id, resource: j.resource}))
            data.wishlist_jobs = data.wishlist_jobs.map(j => ({id: j.id, resource: j.resource}))
            data.wishlist_occupations = data.wishlist_occupations.map(j => ({id: j.id, resource: j.resource}))

            // Formatage des états
            if (data.occupation_skills_goals) {
                data.occupation_skills_goals.map(os => {
                    os.state = typeof os.state == 'object' ? os.state.id : os.state;
                    return os;
                });
            }

            if (data.skills_goals) {
                data.skills_goals.map(os => {
                    os.state = typeof os.state == 'object' ? os.state.id : os.state;
                    return os;
                });
            }

            if (data.others_goals) {
                data.others_goals.map(os => {
                    os.state = typeof os.state == 'object' ? os.state.id : os.state;
                    return os;
                });
                
                // Supprimer les autres objectifs vides
                data.others_goals = data.others_goals.filter(os => !(!os.label && !os.means));
            }

            if (data.comment && Object.keys(data.comment).length) {
                Object.keys(data.comment).forEach(key => {
                    if (!data.comment[key] || data.comment[key] === '') {
                        data.comment[key] = '<br><br><br><br><br><br><br><br><br><br>';
                    }
                })
            }

            delete data.personals
            delete data.externals
           
            if (this.resource.id) {
                method = 'patch';
                url += `/${this.resource.id}`;
            }

            return axios({
                method: method,
                url: url,
                data: data
            })
            .catch(error => {
                console.error(error);
                this.notifyErrorValidation(error);
                this.endLocalLoading()
            });
        },

        redirectBack() {
            document.location.href = this.backUrl
        },

        validateFormHandle(isValid, tabIndex) {
            this.errorValidateMessage = null;
        },

        errorFormHandle(errorValidateMessage) {
            this.errorValidateMessage = errorValidateMessage;
        },

        /**
         * Met à jour le parcours de la personne
         */
        updateCareerValues(resolve, reject) {

            let employee = JSON.parse(JSON.stringify(this.resource.employee));
            employee.jobs = this.resource.perimeter_jobs.map(el => ({id: el.id, resource: el.resource}))
            employee.occupations = this.resource.perimeter_occupations.map(el => ({id: el.id, resource: el.resource}));
            employee.occupation_skills = this.resource.perimeter_occskills.filter(os => os.resource == 'OccupationSkill').map(el => el.id);
            employee.admin_occupation_skills = this.resource.perimeter_occskills.filter(os => os.resource == 'AdminOccupationSkill').map(el => el.id);

            let establishments = {};
            this.resource.employee.establishments.forEach((item, i) => {
                establishments[i] = {
                    id: item.id,
                    start_date: item.pivot?.start_date,
                    pivot_id: item.pivot?.id
                }
            });

            let services = {};
            this.resource.employee.services.forEach((item, i) => {
                services[i] = {
                    id: item.id,
                    etp: item.pivot?.etp,
                    pivot_id: item.pivot?.id
                };
            });

            employee.services = services;
            employee.establishments = establishments;

            window.axios({
                method: 'patch',
                url: '/api/gestion/employees/' + employee.id,
                data: employee
            })
            .then(response => {

                if (response.status === 200 && response.data != '') {
                    this.notifySuccess('Succès', 'Parcours modifié avec succès !')
                    this.careerUpdated = false;
                    resolve(true);
                } else {
                    this.notifyError('Erreur', `Une erreur est survenue lors de la modification du parcours`)
                }
            })
            .catch((err) => {
                this.notifyErrorValidation(err)
            })
        },

        validatePerimeter() {
            document.getElementById("content-area").scrollTop = 0;
            return new Promise((resolve, reject) => {
                if (this.initialLoadingPerimeter) {
                    reject("Les évaluations externes et auto-evaluation sont en cours de chargement. Veuillez patienter.");
                }
                if (!this.resource.rating_date) {
                    reject("Veuillez renseigner tous les champs obligatoires");
                }

                if ((this.typeForm == "creation" ||
                    this.typeForm == "edition" ||
                    this.typeForm == "poursuite") && this.careerUpdated) {
                    this.updateCareerValues(resolve, reject);
                } else {
                    resolve(true);
                }
            });
        },
        validateOccupationSkills() {
            document.getElementById("content-area").scrollTop = 0;
            return new Promise((resolve, reject) => {

                const toValidateOccupationSkills = this.resource.occupations_skills.filter(os => os.to_validate === true);
                if (!this.skillReview && toValidateOccupationSkills.length) {
                    reject("Veuillez valider les éléments requis");
                } else {

                    // Cas d'un retour en arrière sur les étapes, on demande s'il faut réinitialiser les compétences pour
                    // regénérer les valeurs par défaut à partir des savoir-faire
                    // if (this.maxStep > this.currentStep && !this.occupationSkillOnly) {
                    //     this.$swal({
                    //         title: `Retour en arrière détecté`,
                    //         text: "Souhaitez-vous recalculer l'initialisation des compétences à partir des savoir-faire ?",
                    //         type: "warning",
                    //         showCancelButton: true,
                    //         confirmButtonText: "Oui",
                    //         cancelButtonText: "Non"
                    //     }).then(result => {

                    //         if (result.value) {
                    //             this.resource.skills = [];
                    //         }
                    //         resolve(true)
                    //     });
                    // } else {
                    //     //this.resource.skills = [];
                    //     resolve(true)
                    // }
                    resolve(true);

                }
            });
        },
        validateSkills() {
            document.getElementById("content-area").scrollTop = 0;
            return new Promise((resolve, reject) => {

                const toValidateSkills = this.resource.skills.filter(os => os.to_validate === true);
                if (!this.skillReview && toValidateSkills.length) {
                    reject("Veuillez valider les éléments requis");
                } else {
                    resolve(true)
                }
            });
        },
        validateSelfOccupationSkills() {
            document.getElementById("content-area").scrollTop = 0;

            return new Promise((resolve, reject) => {
                resolve(true);
            });
        },
        validateSelfSkills() {
            document.getElementById("content-area").scrollTop = 0;
            return new Promise((resolve, reject) => {

                // Suppression de la popup ticket : MYS-2365
                // Si source mobility (source == 2)
                // if (this.resource.self_skills_source == 2) {
                //     this.$swal({
                //         title: `Confirmez-vous vouloir utiliser l'auto-évaluation Portail de l'usager pour l'auto-évaluation ?`,
                //         text: "",
                //         type: "warning",
                //         showCancelButton: true,
                //         confirmButtonText: "Ok",
                //         cancelButtonText: "Annuler"
                //     }).then(result => {
                //         resolve(!!result.value);
                //     });
                // }

                resolve(true);
            });
        },
        validateGoalsAndSynthesis() {
            document.getElementById("content-area").scrollTop = 0;
            return new Promise((resolve, reject) => {
                const notSelectedOccupationSkillGoal = this.resource.occupation_skills_goals.filter(s => s.occupationSkill == null).length > 0;
                const notSelectedSkillGoal = this.resource.skills_goals.filter(s => s.skill == null).length > 0;

                if (notSelectedOccupationSkillGoal || notSelectedSkillGoal) {
                    reject("Veuillez choisir ou supprimer la ligne d'objectif");
                }

                resolve(true);
            });
        },

        isUserPerimeterService(service) {
            return !!this.user.services.find(s => s.id == service.id)
        },

        loadLastRating() {
            return ratingService.getLastRatings({employee_ids: [this.employee.id], count: 1}).then(response => {
                if (Object.values(response).length > 0) {
                    this.lastRating = response[this.employee.id][0]
                }
                
            })
        },
        initResourceFromLastRating() {

            if (this.lastRating.id) {

                this.resource.hasInternalRatings = true

                if (this.resource.perimeter_occskills.length == 0) {
                    this.resource.perimeter_occskills = [...this.lastRating.perimeter_occskills];
                    this.resource.refresh = 1
                }

                if (this.resource.occupation_skills_goals.length == 0) {
                    this.resource.occupation_skills_goals = [...this.lastRating.occupation_skills_goals.map(g => ({...g, id: null}))];
                }

                if (this.resource.admin_occupation_skills_goals.length == 0) {
                    this.resource.admin_occupation_skills_goals = [...this.lastRating.admin_occupation_skills_goals.map(g => ({...g, id: null}))];
                }

                if (this.resource.skills_goals.length == 0) {
                    this.resource.skills_goals = [...this.lastRating.skills_goals.map(g => ({...g, id: null}))];
                }

                if (this.resource.others_goals.length == 0) {
                    this.resource.others_goals = [...this.lastRating.others_goals.map(g => ({...g, id: null}))];
                    this.resource.others_goals.id == null;
                }
            }
        },

        getStepFromName(name) {
            let step = 0;

            switch (name) {
                case 'perimeter': 
                    step = 0;
                    break;
                case 'occupation-skills': 
                    step = 1;
                    break;
                case 'skills':
                    step = 2;
                    break;
                case 'self-occupation-skills':
                    step = 3;
                    break;
                case 'self-skills':
                    step = this.occupationSkillsSeftRating ? 4 : 3;
                    break;
                case 'goals-and-synthesis':
                    if (this.skillReview) {
                        step = 3
                    } else {
                        step = this.occupationSkillsSeftRating ? 5 : 4
                    }
                    break;
            }

            return step;
        },

        prevClickHandle() {
            this.$refs.formWizard.prevTab();
        },

        nextClickHandle() {
            this.$refs.formWizard.nextTab();
        }
    }
};
</script>

<style lang="scss" scoped>
form {
    padding-top: 1.5em;
}
.switch {
    margin-bottom: -2rem;
}
.bold {
    font-weight: bold;
}
.prefilled {
    background-color: #fafafa;
    border-bottom: 1px solid white;
    p i {
        color: #727272;
    }
}

::v-deep .vue-form-wizard {
    .wizard-title {
        font-weight: bold !important;
    }
    .wizard-custom-title {
        color: #252422;
    }

    // Onglet désactivé
    .disabled-tab {
        pointer-events: none;
        .stepTitle, a {
            color: #cbcbcb !important;
            user-select: none;
        }
        .wizard-icon-circle {
            border: 3px solid #f1f1f1 !important;
            user-select: none;
            i {
                color: #e9e9e9;
            }
        }
    }
}

#saveQuit {
    top: 305px;
    position: fixed; right: 56px;
    z-index: 99999
}

#goBack {
    margin-right: 135px;
}

// Modification de la position du bouton suivant pour DEV
// #next {
//     position: absolute;
//     right: 32px;
//     top: 50px;
// }
</style>

his